import { usePostMiniGamePlay } from '@agos/api-client/marketing'
import { T, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { useState } from 'react'

import {
  gameEmoji,
  gameNote,
  gameTitle,
  mayPlay,
  playableOrPlayed,
  playPayload,
  prizeCoupon,
  prizeEmoji,
  prizeFrom,
  prizeHeadline,
  prizeNote,
  prizePointsNote,
  refusedUntil,
  shouldSaySpent,
  type GameKey,
  type GameStatus,
} from './minigame'
import { ScratchCard } from './ScratchCard'

/**
 * The mini-game cards - one per running game, with its play and its result. `FRONTEND.md` §9.246, §9.264,
 * §9.265, §9.271, and §9.779, owner O138f.
 *
 * Drawn on *Môj profil* and, since §9.779, under *Ďakujeme!* after a web order. One component for both,
 * because the result panel, the scratch card's once-only submit and the refusal are the parts a second copy
 * would get wrong: `POST /mini-game/play` awards points and mints a coupon.
 *
 * `onlyPlayable` is the success page's rule: a game with no play left today is not drawn there, where the
 * profile says when the next one is. A game just played stays, so its result is on screen.
 */
export function MiniGameCards({
  games,
  onPlayed,
  onlyPlayable = false,
}: {
  /** The running games - `runningGames(statusOf(...))`. */
  games: readonly GameStatus[]
  /** Refetch the status: a play used up is a play the cards must stop offering. */
  onPlayed: () => Promise<unknown>
  onlyPlayable?: boolean
}) {
  const playGame = usePostMiniGamePlay()
  // §9.271 - the whole result, not just a caption. Every field below was already in the play response
  // and already parsed; three of the four were being dropped at the point of display.
  const [prize, setPrize] = useState<{
    game: string
    note: string
    headline: string
    emoji: string
    coupon: string | null
    points: string
    won: boolean
  } | null>(null)

  /**
   * Play a game, and say what it gave.
   *
   * **A refusal is a 429 carrying `nextPlayAt`**, and the server says why it bothered: the screen counts
   * down to it, and a bare *no* leaves it counting down to nothing. `refusedUntil` reads that field
   * rather than guessing, and says *tomorrow* when it is absent rather than inventing a clock.
   *
   * The status is refetched afterwards either way - a play used up is a play the screen must stop
   * offering, whether it won or was refused.
   */
  async function play(key: string) {
    setPrize(null)
    try {
      const answer = await playGame.mutateAsync({ data: playPayload(key as GameKey) })
      const won = prizeFrom(answer)
      setPrize({
        game: key,
        note: prizeNote(won),
        headline: prizeHeadline(won),
        emoji: prizeEmoji(won),
        coupon: prizeCoupon(won),
        points: prizePointsNote(won),
        won: won?.won === true,
      })
    } catch (failure) {
      const until = refusedUntil(failure)
      // A refusal is not a result: no emoji, no headline, no code - just the reason, in the panel's
      // quiet form.
      setPrize({
        game: key,
        note: until === null ? tt('Dnešné hranie ste vyčerpali') : `${tt('Ďalšia hra')}: ${until}`,
        headline: '',
        emoji: '',
        coupon: null,
        points: '',
        won: false,
      })
    } finally {
      await onPlayed()
    }
  }

  const shown = onlyPlayable ? playableOrPlayed(games, prize?.game ?? null) : games
  if (shown.length === 0) return null

  return (
    <div className="space-y-2" data-minigames>
      {shown.map((game) => (
        <div
          key={game.key}
          className="rounded-2xl border border-border bg-card p-4 space-y-2"
          data-game={game.key}
          data-game-state={game.state.kind}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl shrink-0" aria-hidden="true">
              {gameEmoji(game.key)}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{gameTitle(game.key)}</p>
              {/* §9.271 - the note stands down while a prize is on screen. It rendered beside a
                * fresh win, so the first thing somebody read after winning was that they could not
                * play; it is true and it is not the news. */}
              {shouldSaySpent(prize !== null && prize.game === game.key, game.state) && (
                <p className="text-xs text-muted-foreground" data-game-note>
                  {gameNote(game.state)}
                </p>
              )}
            </div>
            {/* §9.265, owner O7 - the scratch card is rubbed, not pressed. Legacy has no button on
              * it at all: a canvas that erases under the pointer and submits itself at 60 %. The
              * other game keeps its button, because legacy's *Klikni a vyhraj* is one. */}
            {mayPlay(game.state) && game.key === 'click_and_win' && (
              /* `POST /mini-game/play` awards points and mints a coupon, so it is never called on
                 render - only when this button is pressed. */
              <button
                type="button"
                data-game-play={game.key}
                disabled={playGame.isPending}
                onClick={() => void play(game.key)}
                className="shrink-0 min-h-[36px] px-4 rounded-xl bg-primary text-primary-foreground text-xs font-bold disabled:opacity-50"
              >
                {playGame.isPending ? <T>Hrám…</T> : <T>Hrať</T>}
              </button>
            )}
          </div>
          {mayPlay(game.state) && game.key === 'scratch_card' && (
            <ScratchCard disabled={playGame.isPending} onReveal={() => void play(game.key)} />
          )}

          {/* §9.271, owner O16 - the result as a panel rather than a caption. Legacy's shape: the
            * outcome first and large, then what it is worth, then **the code**, then what was
            * credited. Nothing new is fetched - three of these four fields were already parsed and
            * thrown away at the point of display. */}
          {prize !== null && prize.game === game.key && (
            <div
              data-game-result={game.key}
              className={cn(
                'rounded-xl border p-3 text-center space-y-1.5',
                prize.won
                  ? 'border-emerald-500/40 bg-emerald-500/10'
                  : 'border-border bg-muted/40',
              )}
            >
              <div className="text-3xl" aria-hidden="true">
                {prize.emoji}
              </div>
              <p
                className={cn(
                  'font-bold text-base',
                  prize.won ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
                )}
                data-game-headline
              >
                {prize.headline}
              </p>
              <p className="text-sm font-semibold" data-game-prize={game.key}>
                {prize.note}
              </p>
              {prize.coupon !== null && (
                <div
                  data-game-coupon
                  className="mx-auto inline-block rounded-lg border-2 border-dashed border-emerald-500/60 bg-background px-4 py-2 font-mono font-bold tracking-widest text-emerald-600 dark:text-emerald-400"
                >
                  {prize.coupon}
                </div>
              )}
              {prize.points !== '' && (
                <p className="text-xs text-muted-foreground" data-game-points>
                  {prize.points}
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
